// React
import React, { useState, useEffect } from "react";

// Material Components
import { Autocomplete, InputAdornment } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import FontComponent from "components/Responsive/fonts";

// Common Components
import pxToRem from "assets/theme/functions/pxToRem";

// Utils
import { Icons, Common } from "utils/Constants";

// 3rd party
import PropTypes from "prop-types";

/**
 * Explicit Search Bar Component
 *
 * A search component that triggers search only on explicit user action:
 * - Pressing Enter key
 * - Clicking the search icon
 *
 * Unlike the standard SearchInTable component which uses debounced/instant search,
 * this component does NOT trigger API calls while typing, providing better control
 * over when searches are executed and reducing unnecessary API calls.
 *
 * Use this component when you want users to explicitly trigger searches rather than
 * automatic filtering on each keystroke.
 */
export default function ExplicitSearchBar({
  filters,
  handleFilterChange,
  options,
  value,
  placeholder,
  label = "Search",
  width = pxToRem(300),
  marginRight = 2,
  freeSolos,
  error,
  helperText,
  disabled,
  isLoading = false,
  onSearchTrigger, // Trigger search explicitly (Enter or icon click)
  allowEmptySearch = false,
}) {
  const [open, setOpen] = useState(false); // State to manage dropdown visibility
  const [inputValue, setInputValue] = useState(""); // Track the current input value
  const fontSize = FontComponent({ sizes: {} });

  // Sync inputValue with the value prop when it changes (e.g., on page refresh)
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleKeyDown = (event) => {
    if (event.key === Common.ENTER_KEY || event.key === "Enter") {
      setOpen(false); // Close the dropdown when Enter is pressed
      // Trigger search on Enter key press
      if (onSearchTrigger) {
        const trimmedValue = inputValue?.trim();
        if (allowEmptySearch || (trimmedValue && trimmedValue.length > 0)) {
          onSearchTrigger(trimmedValue);
        }
      }
    }
  };

  // Handle search icon click
  const handleSearchIconClick = () => {
    if (onSearchTrigger) {
      const trimmedValue = inputValue?.trim();
      onSearchTrigger(trimmedValue);
    }
  };

  // Handle onChange from Autocomplete (when user selects from dropdown)
  const handleAutocompleteChange = (event, newValue, reason) => {
    // If user selected from dropdown, call the original handleFilterChange
    if (reason === "selectOption" && handleFilterChange) {
      handleFilterChange(event, newValue);
    }
    // If reason is "createOption" (Enter pressed in freeSolo mode), trigger search
    else if (reason === "createOption" && onSearchTrigger) {
      const trimmedValue = newValue?.trim() || inputValue?.trim();
      if (allowEmptySearch || (trimmedValue && trimmedValue.length > 0)) {
        onSearchTrigger(trimmedValue);
      }
    }
  };

  // Function to preprocess the input value
  const preprocessInputValue = (values) => values.trim(); // Remove leading and trailing spaces

  return (
    <MDBox display="flex" alignItems="start" sx={{ flexDirection: "column", marginRight, mt: 2.5 }}>
      <MDTypography variant="caption" mb={1} sx={{ fontSize, fontWeight: 600, color: "#344054" }}>
        {label}
      </MDTypography>
      <Autocomplete
        disablePortal
        freeSolo={freeSolos}
        disabled={disabled}
        id="personnel-search-combo-box"
        options={options}
        value={value}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        noOptionsText="No data found"
        open={open} // Control the dropdown visibility
        onOpen={() => setOpen(true)} // Open the dropdown
        onClose={() => setOpen(false)} // Close the dropdown
        onKeyDown={handleKeyDown} // Handle keydown events
        filterOptions={(option, state) => {
          const trimmedInputValue = preprocessInputValue(state.inputValue);
          const search = trimmedInputValue?.toLowerCase() || "";
          if (!search) return option;
          const getRank = (item) => {
            const title = item?.toLowerCase() || "";
            if (title === search) return 0;
            if (title.startsWith(search)) return 1;
            if (title.includes(search)) return 2;
            return 3;
          };
          return option
            .filter((option2) => option2?.toLowerCase()?.includes(search))
            .sort((a, b) => getRank(a) - getRank(b));
        }}
        renderOption={(props, option) => (
          <MDBox
            component="li"
            sx={{
              fontSize,
              fontWeight: 500,
              color: "#344054",
              textTransform: "capitalize",
            }}
            {...props}
          >
            {option}
          </MDBox>
        )}
        sx={{
          width,
          "& .MuiOutlinedInput-root": {
            padding: 0,
            height: 40,
            backgroundColor: "#fff",
            paddingX: "8px !important",
            fontSize,
            fontWeight: 600,
            color: "#344054",
          },
        }}
        renderInput={(params) => (
          <MDInput
            {...params}
            placeholder={placeholder}
            pr={0}
            error={error}
            helperText={helperText}
            FormHelperTextProps={{
              sx: { marginLeft: 0, marginTop: 1, color: "red" },
            }}
            sx={{ textTransform: "capitalize" }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    cursor:
                      allowEmptySearch || inputValue?.trim().length > 0 ? "pointer" : "default",
                    opacity: allowEmptySearch || inputValue?.trim().length > 0 ? 1 : 0.5,
                    pointerEvents:
                      allowEmptySearch || inputValue?.trim().length > 0 ? "auto" : "none",
                  }}
                  onClick={
                    allowEmptySearch || inputValue?.trim().length > 0
                      ? handleSearchIconClick
                      : undefined
                  }
                >
                  {filters && isLoading ? Icons.LOADING : Icons.SEACRH}
                </InputAdornment>
              ),
            }}
          />
        )}
        onChange={handleAutocompleteChange}
      />
    </MDBox>
  );
}

ExplicitSearchBar.defaultProps = {
  filters: false,
  handleFilterChange: () => {},
  options: [],
  value: "",
  placeholder: "",
  label: "Search",
  width: pxToRem(300),
  marginRight: 2,
  freeSolos: false,
  error: false,
  helperText: "",
  disabled: false,
  isLoading: false,
  onSearchTrigger: null,
  allowEmptySearch: false,
};

ExplicitSearchBar.propTypes = {
  filters: PropTypes.bool,
  handleFilterChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.string,
  marginRight: PropTypes.number,
  freeSolos: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSearchTrigger: PropTypes.func,
  allowEmptySearch: PropTypes.bool,
};
